import { useEffect, useState, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getUnverifiedUsers,
  verifyUsers,
} from "../../store/admin/requestSlice";
import { fetchFilteredUsersByEmailAndRole } from "../../store/common/searchSlice";
import { useToast } from "../../components/utilities/Toast";
import { Helmet } from "react-helmet-async";
import { User } from "../../types/User";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminMetaRow,
  AdminPagination,
  AdminTableCard,
  AdminTableState,
  adminTableHead,
  adminTableCell,
  formatAdminDateTime,
} from "../../components/admin/AdminPageShell";

const ApproveButton = ({ approved, onClick, disabled, label }: any) => (
  <button
    type="button"
    disabled={disabled || approved}
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center justify-center whitespace-nowrap ${
      approved
        ? "bg-emerald-500 text-white shadow-sm"
        : "bg-[#F0F7F8] text-[#2fa9bf] ring-1 ring-[#D4EBEF] hover:bg-[#E5F4F7]"
    } ${
      disabled && !approved ? "opacity-50 cursor-not-allowed" : ""
    } ${approved ? "cursor-default" : "cursor-pointer"}`}
  >
    {approved ? `✓ ${label}` : `Approve ${label}`}
  </button>
);

const ConfirmModal = ({ open, onClose, onConfirm, userName, portalLabel }: any) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 border border-[#E8E8E4]">
        <h2 className="text-base font-semibold text-[#4A4A48]">
          Confirm Approval
        </h2>
        <p className="text-sm text-[#8A8A85] leading-relaxed">
          Are you sure you want to grant{" "}
          <span className="font-medium text-[#4A4A48]">{userName}</span>{" "}
          access to the{" "}
          <span className="font-medium text-[#2fa9bf]">{portalLabel}</span>?
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-xl bg-[#2fa9bf] hover:bg-[#2296aa] text-white text-xs font-medium transition"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const Requests = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const { user } = useAppSelector((state) => state.auth);
  const { unverifiedUsers, currentPage, totalPages, isLoading } =
    useAppSelector((state) => state.adminRequestControls);
  const { filteredUsers } = useAppSelector((state) => state.search);

  const [searchTerm, setSearchTerm] = useState("");
  const isFirstRender = useRef(true);

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedPortal, setSelectedPortal] = useState<
    "cases" | "smile" | "both"
  >("cases");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isReadOnly = user?.role === "Admin Read";
  const portalLabel =
    selectedPortal === "cases"
      ? "Cases Portal"
      : selectedPortal === "smile"
        ? "Smile Portal"
        : "Cases & Smile Portals";
  const entriesPerPage = 25;

  const rawRequests = searchTerm.trim()
    ? (filteredUsers as any)?.users || []
    : unverifiedUsers;

  // A user drops out of requests as soon as they have any portal access.
  const requests = rawRequests.filter(
    (u: any) => !u.verified && !u.verifySmile
  );

  const isSearching = !!searchTerm.trim();

  // Initial fetch
  useEffect(() => {
    if (!unverifiedUsers.length) {
      dispatch(
        getUnverifiedUsers({ page: 1, limit: entriesPerPage, search: "" })
      );
    }
  }, [dispatch, unverifiedUsers.length]);

  // Debounced search
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const handler = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed) {
        dispatch(fetchFilteredUsersByEmailAndRole({ email: trimmed }));
      } else {
        dispatch(
          getUnverifiedUsers({
            page: currentPage,
            limit: entriesPerPage,
            search: "",
          })
        );
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [dispatch, currentPage, searchTerm]);

  // Approval logic (after modal confirmation)
  const handleApproveConfirmed = useCallback(() => {
    if (!selectedUser || isReadOnly) return;

    const update: {
      user_id: number;
      verified?: boolean;
      verifySmile?: boolean;
    } = { user_id: selectedUser.user_id };

    if (selectedPortal === "cases" || selectedPortal === "both") {
      update.verified = true;
    }
    if (selectedPortal === "smile" || selectedPortal === "both") {
      update.verifySmile = true;
    }

    dispatch(verifyUsers({ updateData: [update] })).then(() => {
      // Search results live in a separate slice, so refresh them explicitly.
      if (searchTerm.trim()) {
        dispatch(fetchFilteredUsersByEmailAndRole({ email: searchTerm.trim() }));
      }
    });

    showToast(
      `${selectedUser.user_name} granted ${portalLabel} access`,
      "success"
    );

    setIsModalOpen(false);
  }, [
    dispatch,
    selectedUser,
    selectedPortal,
    isReadOnly,
    searchTerm,
    portalLabel,
    showToast,
  ]);

  const handleApproveClick = (
    user: any,
    portal: "cases" | "smile" | "both"
  ) => {
    setSelectedUser(user);
    setSelectedPortal(portal);
    setIsModalOpen(true);
  };

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage < 1 || newPage > totalPages) return;
      dispatch(
        getUnverifiedUsers({ page: newPage, limit: entriesPerPage, search: "" })
      );
    },
    [dispatch, totalPages]
  );

  const colCount = 11;

  return (
    <>
      <Helmet>
        <title>Requests</title>
      </Helmet>

      <AdminPageShell>
        {/* Confirm Modal */}
        <ConfirmModal
          open={isModalOpen}
          userName={selectedUser?.user_name}
          portalLabel={portalLabel}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleApproveConfirmed}
        />

        {/* Header */}
        <AdminPageHeader
          title="Requests"
          subtitle="Review signup requests and grant portal access"
        >
          <AdminSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by Email…"
          />
        </AdminPageHeader>

        {/* Meta row */}
        <AdminMetaRow
          left={
            <>
              {isLoading ? (
                <span>Loading users…</span>
              ) : (
                <span>
                  {requests.length} request{requests.length === 1 ? "" : "s"}
                  {isSearching ? " matched" : " on this page"}
                </span>
              )}
              {isReadOnly ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2.5 py-0.5">
                  Read-Only Access
                </span>
              ) : null}
            </>
          }
          right={
            totalPages > 1 && !isSearching ? (
              <AdminPagination
                page={currentPage}
                totalPages={totalPages}
                onPrev={() => handlePageChange(currentPage - 1)}
                onNext={() => handlePageChange(currentPage + 1)}
                disabled={isLoading}
              />
            ) : undefined
          }
        />

        {/* Table */}
        <AdminTableCard>
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className={adminTableHead}>ID</th>
                <th className={adminTableHead}>Name</th>
                <th className={adminTableHead}>Email</th>
                <th className={adminTableHead}>Organisation</th>
                <th className={adminTableHead}>Address</th>
                <th className={adminTableHead}>Phone Number</th>
                <th className={adminTableHead}>Post Code</th>
                <th className={adminTableHead}>Partner</th>
                <th className={adminTableHead}>Token</th>
                <th className={adminTableHead}>Created</th>
                <th className={`${adminTableHead} text-center`}>
                  Portal Access
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="loading"
                  message="Loading users…"
                />
              ) : !requests.length ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="empty"
                  message="No requests found"
                />
              ) : (
                requests.map((item: User) => (
                  <tr
                    key={item.user_id}
                    className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] transition-colors"
                  >
                    <td className={adminTableCell}>{item.user_id}</td>
                    <td className={adminTableCell}>{item.user_name}</td>
                    <td className={adminTableCell}>{item.email}</td>
                    <td className={adminTableCell}>
                      {item.organisation ? item.organisation : `N/A`}
                    </td>
                    <td className={adminTableCell}>
                      {item.address ? item.address : `N/A`}
                    </td>
                    <td className={adminTableCell}>
                      {item.phone_number ? item.phone_number : `N/A`}
                    </td>
                    <td className={adminTableCell}>
                      {item.post_code ? item.post_code : `N/A`}
                    </td>
                    <td className={adminTableCell}>
                      {item.partner ? item.partner : `N/A`}
                    </td>
                    <td className={adminTableCell}>
                      {item.token ? item.token : `N/A`}
                    </td>
                    <td className={adminTableCell}>
                      {formatAdminDateTime(item.created_at)}
                    </td>
                    <td className={adminTableCell}>
                      <div className="flex justify-center gap-2 flex-wrap">
                        <ApproveButton
                          label="Cases"
                          approved={!!item.verified}
                          onClick={() => handleApproveClick(item, "cases")}
                          disabled={isReadOnly || !item.token}
                        />
                        <ApproveButton
                          label="Smile"
                          approved={!!item.verifySmile}
                          onClick={() => handleApproveClick(item, "smile")}
                          disabled={isReadOnly || !item.token}
                        />
                        <ApproveButton
                          label="Both"
                          approved={!!item.verified && !!item.verifySmile}
                          onClick={() => handleApproveClick(item, "both")}
                          disabled={isReadOnly || !item.token}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableCard>
      </AdminPageShell>
    </>
  );
};

export default Requests;
